import { useQuery, useQueryClient } from '@tanstack/react-query'
import { useState } from 'react'
import Badge from 'react-bootstrap/Badge'
import Button from 'react-bootstrap/Button'
import Card from 'react-bootstrap/Card'
import ListGroup from 'react-bootstrap/ListGroup'
import Spinner from 'react-bootstrap/Spinner'
import { deleteUser, listUsers, type User } from '../../../../types'
import { actionApiCall } from '../../api/actionApiCall'
import { useAuth } from '../../auth/AuthContext'
import { formatDate } from '../../utils/format'
import { UserFormModal } from './UserFormModal'

export function UsersPage() {
    const queryClient = useQueryClient()
    const { user: me } = useAuth()
    const [editing, setEditing] = useState<User | undefined>()
    const [showForm, setShowForm] = useState(false)

    const { data: users, isLoading } = useQuery({
        queryKey: ['users'],
        queryFn: () => actionApiCall(() => listUsers()),
    })

    const reload = () => queryClient.invalidateQueries({ queryKey: ['users'] })

    const handleDelete = async (user: User) => {
        if (!window.confirm(`Opravdu smazat uživatele ${user.displayName}?`)) {
            return
        }
        const ok = await actionApiCall(() => deleteUser({ path: { userId: user.id } }), { successMessage: 'Uživatel byl smazán.' })
        if (ok !== undefined) {
            await reload()
        }
    }

    return (
        <>
            <div className="d-flex align-items-center justify-content-between mb-3">
                <h1 className="h4 mb-0">Uživatelé</h1>
                <Button
                    className="su-tappable"
                    onClick={() => {
                        setEditing(undefined)
                        setShowForm(true)
                    }}
                >
                    <i className="bi bi-plus-lg me-1" aria-hidden="true" />
                    Nový
                </Button>
            </div>

            {isLoading && <Spinner animation="border" />}

            {users && (
                <Card>
                    <ListGroup variant="flush">
                        {users.map((user) => (
                            <ListGroup.Item key={user.id} className="d-flex align-items-center gap-2">
                                <div className="flex-grow-1 min-w-0">
                                    <div className="fw-semibold text-truncate">
                                        {user.displayName}
                                        {user.id === me?.id && <span className="text-secondary fw-normal"> (vy)</span>}
                                    </div>
                                    <div className="small text-secondary text-truncate">
                                        {user.login} · založen {formatDate(user.createdAt)}
                                    </div>
                                    <div className="mt-1 d-flex gap-1 flex-wrap">
                                        {user.isAdmin && <Badge bg="primary">Administrátor</Badge>}
                                        {!user.isActive && <Badge bg="secondary">Neaktivní</Badge>}
                                        {user.mustChangePassword && <Badge bg="warning" text="dark">Dočasné heslo</Badge>}
                                    </div>
                                </div>
                                <Button
                                    variant="outline-secondary"
                                    size="sm"
                                    className="su-tappable"
                                    aria-label={`Upravit ${user.displayName}`}
                                    onClick={() => {
                                        setEditing(user)
                                        setShowForm(true)
                                    }}
                                >
                                    <i className="bi bi-pencil" aria-hidden="true" />
                                </Button>
                                <Button
                                    variant="outline-danger"
                                    size="sm"
                                    className="su-tappable"
                                    aria-label={`Smazat ${user.displayName}`}
                                    disabled={user.id === me?.id}
                                    onClick={() => void handleDelete(user)}
                                >
                                    <i className="bi bi-trash" aria-hidden="true" />
                                </Button>
                            </ListGroup.Item>
                        ))}
                        {users.length === 0 && <ListGroup.Item className="text-secondary">Zatím žádní uživatelé.</ListGroup.Item>}
                    </ListGroup>
                </Card>
            )}

            {showForm && (
                <UserFormModal
                    show={showForm}
                    // Klíč vynutí čerstvý stav formuláře při přepnutí mezi uživateli.
                    key={editing?.id ?? 'novy'}
                    user={editing}
                    onClose={() => setShowForm(false)}
                    onSaved={() => void reload()}
                />
            )}
        </>
    )
}
